import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import PageHeader from '@spark/ui-components/components/BreadCrumb';
import Icon from '@spark/ui-components/components/Icon';
import styled from 'styled-components';
import AtrasoDescargaCard from './components/AtrasoDescargaCard';
import ChegadasForaJanelaCard from './components/ChegadasForaJanelaCard';
import DashboardFilters from './components/DashboardFilters';
import DashboardSettingsModal from './components/DashboardSettingsModal';
import LojasComMaisAtrasosCard from './components/LojasComMaisAtrasosCard';
import LojasForaJanelaCard from './components/LojasForaJanelaCard';
import MediaTempoEsperaCard from './components/MediaTempoEsperaCard';
import RankingMediaTempoLojaCard from './components/RankingMediaTempoLojaCard';
import RankingMediaTempoLojaTabelaCard from './components/RankingMediaTempoLojaTabelaCard';
import TempoMedioForaJanelaCard from './components/TempoMedioForaJanelaCard';
import TotalCargasUltimosDiasCard from './components/TotalCargasUltimosDiasCard';
import {
  DASHBOARD_CARD_VISIBILITY_STORAGE_KEY,
  DASHBOARD_TIMER_SETTINGS_STORAGE_KEY,
  dashboardCardVisibilityOptions,
  getDefaultDashboardCardVisibility,
  normalizeDashboardCardVisibility,
  normalizeDashboardTimerSettings
} from './config/cardVisibilityConfig';
import { useDescargaDashboard } from './hooks/useDescargaDashboard';
import { usePresentationFullscreen } from '../shared/hooks/usePresentationFullscreen';

const dashboardCardDefinitions = dashboardCardVisibilityOptions;

const loadCardVisibility = (cardOptions = dashboardCardDefinitions) => {
  const defaultVisibility = getDefaultDashboardCardVisibility(cardOptions);

  if (typeof window === 'undefined' || !window.localStorage) {
    return defaultVisibility;
  }

  try {
    const storedValue = window.localStorage.getItem(DASHBOARD_CARD_VISIBILITY_STORAGE_KEY);
    if (!storedValue) return defaultVisibility;

    return normalizeDashboardCardVisibility(JSON.parse(storedValue), cardOptions);
  } catch (error) {
    return defaultVisibility;
  }
};

const loadTimerSettings = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return normalizeDashboardTimerSettings();
  }

  try {
    const storedValue = window.localStorage.getItem(DASHBOARD_TIMER_SETTINGS_STORAGE_KEY);
    if (!storedValue) return normalizeDashboardTimerSettings();

    return normalizeDashboardTimerSettings(JSON.parse(storedValue));
  } catch (error) {
    return normalizeDashboardTimerSettings();
  }
};

const getTimerSeconds = (settings) => normalizeDashboardTimerSettings(settings).intervalMinutes * 60;

const DescargaDashboardMain = () => {
  const [showSettings, setShowSettings] = useState(false);
  const [cardVisibility, setCardVisibility] = useState(() => loadCardVisibility(dashboardCardDefinitions));
  const [timerSettings, setTimerSettings] = useState(loadTimerSettings);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerRemainingSeconds, setTimerRemainingSeconds] = useState(() => getTimerSeconds(loadTimerSettings()));
  const fullscreenCardsRef = useRef(null);
  const { isFullscreen, toggleFullscreen: handleToggleFullscreen } = usePresentationFullscreen();
  const {
    filters,
    lojasComMaisAtrasos,
    rankingMediaTempoLoja,
    rankingMediaTempoLojaTabela,
    rankingMediaTempoLojaTotalRegistros,
    rankingMediaTempoLojaPagination,
    rankingMediaTempoLojaLoading,
    tempoEsperaMedio,
    atrasosDescarga,
    chegadasForaJanela,
    lojasForaJanela,
    todasCargasUltimosDias,
    todasCargasBlockedMessage,
    loading,
    handleApplyFilters,
    handleClearFilters,
    handleRefreshDashboard,
    handleRankingMediaTempoLojaPaginationChange
  } = useDescargaDashboard();

  useEffect(() => {
    setTimerRemainingSeconds(getTimerSeconds(timerSettings));
  }, [timerSettings]);

  useEffect(() => {
    if (!timerRunning) return undefined;

    const intervalId = window.setInterval(() => {
      setTimerRemainingSeconds((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [timerRunning]);

  useEffect(() => {
    if (!timerRunning || timerRemainingSeconds > 0) return;

    handleRefreshDashboard();
    setTimerRemainingSeconds(getTimerSeconds(timerSettings));
  }, [handleRefreshDashboard, timerRemainingSeconds, timerRunning, timerSettings]);

  const handleSaveSettings = (nextVisibility) => {
    const normalizedVisibility = normalizeDashboardCardVisibility(nextVisibility, dashboardCardDefinitions);
    setCardVisibility(normalizedVisibility);
    setShowSettings(false);

    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        DASHBOARD_CARD_VISIBILITY_STORAGE_KEY,
        JSON.stringify(normalizedVisibility)
      );
    }
  };

  const handleSaveTimerSettings = (nextTimerSettings) => {
    const normalizedTimerSettings = normalizeDashboardTimerSettings(nextTimerSettings);
    setTimerSettings(normalizedTimerSettings);

    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        DASHBOARD_TIMER_SETTINGS_STORAGE_KEY,
        JSON.stringify(normalizedTimerSettings)
      );
    }
  };

  const handleToggleTimer = useCallback(() => {
    setTimerRunning((current) => {
      const nextRunning = !current;
      if (nextRunning && timerRemainingSeconds <= 0) {
        setTimerRemainingSeconds(getTimerSeconds(timerSettings));
      }

      return nextRunning;
    });
  }, [timerRemainingSeconds, timerSettings]);

  const handleRefreshTimerNow = useCallback(() => {
    handleRefreshDashboard();
    setTimerRemainingSeconds(getTimerSeconds(timerSettings));
  }, [handleRefreshDashboard, timerSettings]);

  const dashboardCards = dashboardCardDefinitions.map((card) => ({
    ...card,
    render: {
      rankingMediaTempoLoja: () => (
        <RankingMediaTempoLojaCard
          data={rankingMediaTempoLoja}
          loading={loading}
        />
      ),
      rankingMediaTempoLojaTabela: () => (
        <RankingMediaTempoLojaTabelaCard
          data={rankingMediaTempoLojaTabela}
          loading={loading || rankingMediaTempoLojaLoading}
          totalRegistros={rankingMediaTempoLojaTotalRegistros}
          paginationState={rankingMediaTempoLojaPagination}
          onPaginationChange={handleRankingMediaTempoLojaPaginationChange}
        />
      ),
      lojasComMaisAtrasos: () => (
        <LojasComMaisAtrasosCard
          data={lojasComMaisAtrasos}
          loading={loading}
        />
      ),
      mediaTempoEspera: () => (
        <MediaTempoEsperaCard
          data={tempoEsperaMedio}
          loading={loading}
        />
      ),
      atrasoDescarga: () => (
        <AtrasoDescargaCard
          data={atrasosDescarga}
          loading={loading}
        />
      ),
      chegadasForaJanela: () => (
        <ChegadasForaJanelaCard
          data={chegadasForaJanela}
          loading={loading}
        />
      ),
      tempoMedioForaJanela: () => (
        <TempoMedioForaJanelaCard
          data={chegadasForaJanela}
          loading={loading}
        />
      ),
      lojasForaJanela: () => (
        <LojasForaJanelaCard
          data={lojasForaJanela}
          loading={loading}
        />
      ),
      totalCargasUltimosDias: () => (
        <TotalCargasUltimosDiasCard
          data={todasCargasUltimosDias}
          loading={loading}
          blockedMessage={todasCargasBlockedMessage}
        />
      )
    }[card.id]
  })).filter((card) => typeof card.render === 'function');
  const visibleDashboardCards = dashboardCards.filter((card) => cardVisibility[card.id] !== false);
  const visibleTopCards = visibleDashboardCards.filter((card) => card.group === 'top');
  const hasFeaturedTopCard = visibleTopCards.some((card) => card.variant === 'featured');
  const getCardSpan = (card) => {
    if (card.group === 'full') return 12;
    if (card.group === 'janela') return card.span || 4;
    if (visibleTopCards.length <= 1) return 12;

    if (card.variant === 'featured') {
      return visibleTopCards.length === 2 ? 8 : 6;
    }

    if (hasFeaturedTopCard) {
      return visibleTopCards.length === 2 ? 4 : 3;
    }

    return Math.max(3, Math.floor(12 / Math.min(visibleTopCards.length, 4)));
  };

  return (
    <Container fluid>
      <Row>
        <Col md={12}>
          <PageHeader
            title=""
            routeSegments={[
              { name: 'Inicio' },
              { name: 'Descarga' },
              { name: 'Dashboard' }
            ]}
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <DashboardFilters
            values={filters}
            loading={loading}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
          />
        </Col>
      </Row>

      <SettingsActionRow>
        <SettingsButton
          type="button"
          onClick={handleToggleFullscreen}
          aria-label="Alternar tela cheia dos cards"
          title="Alternar tela cheia dos cards"
        >
          <Icon name={isFullscreen ? 'fullscreen-exit' : 'fullscreen'} size={16} />
        </SettingsButton>
        <SettingsButton
          type="button"
          onClick={() => setShowSettings(true)}
          aria-label="Configurar dashboard"
          title="Configurar dashboard"
        >
          <Icon name="settings" size={16} />
        </SettingsButton>
      </SettingsActionRow>

      <FullscreenCardsArea ref={fullscreenCardsRef} $isFullscreen={isFullscreen}>
        {visibleDashboardCards.length > 0 && (
          <DashboardCardsGrid>
            {visibleDashboardCards.map((card) => (
              <DashboardCardSlot key={card.id} $span={getCardSpan(card)}>
                {card.render()}
              </DashboardCardSlot>
            ))}
          </DashboardCardsGrid>
        )}

        {visibleDashboardCards.length === 0 && (
          <NoCardsMessage>
            Nenhum card selecionado para exibicao.
          </NoCardsMessage>
        )}
      </FullscreenCardsArea>

      <DashboardSettingsModal
        show={showSettings}
        values={cardVisibility}
        timerSettings={timerSettings}
        timerRunning={timerRunning}
        timerRemainingSeconds={timerRemainingSeconds}
        timerLoading={loading}
        cardOptions={dashboardCardDefinitions}
        onClose={() => setShowSettings(false)}
        onSaveCards={handleSaveSettings}
        onSaveTimer={handleSaveTimerSettings}
        onToggleTimer={handleToggleTimer}
        onRefreshTimer={handleRefreshTimerNow}
      />
    </Container>
  );
};

export default DescargaDashboardMain;

const SettingsActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: -12px;
`;

const SettingsButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #d8dee9;
  border-radius: 4px;
  background: #ffffff;
  color: #536179;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #233980;
    color: #233980;
    background: #f7f9ff;
  }
`;

const DashboardCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 24px;
  margin-top: 16px;

  @media (max-width: 1199px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const DashboardCardSlot = styled.div`
  grid-column: span ${({ $span }) => $span};
  min-width: 0;

  @media (max-width: 1199px) {
    grid-column: span 6;
  }

  @media (max-width: 767px) {
    grid-column: span 1;
  }
`;

const NoCardsMessage = styled.div`
  margin-top: 16px;
  padding: 24px;
  border: 1px dashed #cfd8e3;
  background: #ffffff;
  color: #6b7280;
  text-align: center;
  font-size: 13px;
`;

const FullscreenCardsArea = styled.div`
  ${({ $isFullscreen }) => $isFullscreen && `
    position: fixed;
    inset: 0;
    z-index: 9999;
    width: 100vw;
    min-height: 100vh;
    padding: 24px;
    overflow: auto;
    background: #f2f5fa;
    color: #121212;

    .card {
      box-shadow: 0 8px 24px rgba(34, 56, 101, 0.12);
    }
  `}
`;
